import React, { useEffect, useState } from "react";
import {
  conflictRules,
  createPositionCode,
  deletePositionCode,
  getPositionCodes,
  getServiceIDsForPositionCode,
  positionCodeEquals,
  setsEqual,
  updatePositionCode,
  updateServicesForPositionCode,
} from "../../services/positionCodeService";
import { checkForms, getClientByEmail } from "../../services/clientService";
import { useAuth } from "../../auth";
import { PositionCode } from "../../interfaces/positionCode";
import { Service } from "../../interfaces/service";
import { getAllServices } from "../../services/serviceService";
import "./PositionCodes.css";
import { Client } from "../../interfaces/client";
import ConfirmDelete from "../../components/component-tools/ConfirmDelete";
import { notifyAdminOfClientUpdate } from "../../services/emailService";
import { sortServices } from "../../services/helpers";
import NotificationsPopup from "../../components/client-components/NotificationsPopup";
import { ClientNotification } from "../../interfaces/clientNotification";
import { getNotifications } from "../../services/notificationsService";
import ConfirmPopup from "../../components/client-components/ConfirmPopup";

const PositionCodes = () => {
  const [errorMessage, setErrorMessage] = useState("");
  const { decodeToken } = useAuth();
  const [OGpositionCodes, setOGPositionCodes] = useState<PositionCode[] | null>(
    null,
  );
  const [positionCodes, setPositionCodes] = useState<PositionCode[] | null>(
    null,
  );
  const [allServices, setAllServices] = useState<Service[]>([]);
  const [OGselectedServices, setOGSelectedServices] = useState<
    Map<number, Set<number>>
  >(new Map());
  const [selectedServices, setSelectedServices] = useState<
    Map<number, Set<number>>
  >(new Map());
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string;
  }>({});
  const [successMessage, setSuccessMessage] = useState("");
  const [client, setClient] = useState<Client | null>(null);
  const [showAbbreviation, setShowAbbreviation] = useState(false);
  const [loading, setLoading] = useState(true);
  const [localPositionCodeIDs, setLocalPositionCodeIDs] = useState<Set<number>>(
    new Set(),
  );
  const [loadingSpinner, setLoadingSpinner] = useState(false);
  const [notifications, setNotifications] = useState<ClientNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);
  const [editing, setEditing] = useState(false);
  const [monitoringToRevert, setMonitoringToRevert] = useState<{
    positionCodeID: number;
    serviceID: number;
  } | null>(null);

  useEffect(() => {
    const loadPositionCodes = async () => {
      try {
        const email = decodeToken(localStorage.getItem("token") || "");
        const clientIDString = localStorage.getItem("clientID");
        const clientID = clientIDString ? parseInt(clientIDString, 10) : 0;
        const client = await getClientByEmail(email, clientID);
        setClient(client);

        const [showAbbreviationResponse] = await checkForms(client.clientID);
        setShowAbbreviation(showAbbreviationResponse);

        if (client.clientID) {
          const positionCodes = await getPositionCodes(client.clientID);
          setPositionCodes(positionCodes);
          setOGPositionCodes(positionCodes);
          const allServices = await getAllServices();
          const sortedServices = await sortServices(allServices);
          const filteredServices = sortedServices.filter(
            (service) => service.clientVisible === true,
          );
          setAllServices(filteredServices);

          const serviceMap = new Map();
          for (const positionCode of positionCodes) {
            const serviceIDs = await getServiceIDsForPositionCode(
              positionCode.positionCodeID,
            );
            serviceMap.set(positionCode.positionCodeID, new Set(serviceIDs));
          }
          setSelectedServices(serviceMap);
          setOGSelectedServices(serviceMap);

          const notificationsResponse = await getNotifications(
            client?.clientID,
          );
          notificationsResponse.sort(
            (a, b) =>
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
          );
          setNotifications(notificationsResponse);

          const unreadCount = notificationsResponse.length;
          setUnreadCount(unreadCount);
        }
      } catch (error) {
        console.error("Error fetching position codes or services:", error);
        setErrorMessage("Error fetching data.");
      } finally {
        setLoading(false);
      }
    };

    loadPositionCodes();
  }, [decodeToken]);

  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => setErrorMessage(""), 3000);
      return () => clearTimeout(timer);
    }
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage, successMessage]);

  const handleCheckboxChange = (positionCodeID: number, serviceID: number) => {
    setEditing(true);
    setSelectedServices((prevSelected) => {
      const newSelected = new Map(prevSelected);
      const selectedServiceSet = new Set(newSelected.get(positionCodeID) || []);

      const isSelected = selectedServiceSet.has(serviceID);
      const conflictingServices = conflictRules[serviceID] || [];

      if (!isSelected) {
        const foundConflict = conflictingServices.find((conflictID) =>
          selectedServiceSet.has(conflictID),
        );

        if (foundConflict) {
          setValidationErrors((prevErrors) => ({
            ...prevErrors,
            [positionCodeID]: `You have a conflicting selected service.`,
          }));
          return prevSelected;
        }

        // confirm that they want monitoring
        if (serviceID === 36) {
          setMonitoringToRevert({ positionCodeID, serviceID });
        }
      }

      if (isSelected) {
        selectedServiceSet.delete(serviceID);
      } else {
        selectedServiceSet.add(serviceID);
      }

      newSelected.set(positionCodeID, selectedServiceSet);

      setValidationErrors((prevErrors) => ({
        ...prevErrors,
        [positionCodeID]: "",
      }));

      return newSelected;
    });
  };

  const handleSave = async () => {
    try {
      setLoadingSpinner(true);
      const errors: Record<string, string> = {};

      if (!positionCodes || positionCodes.length === 0 || !client) return;

      for (let i = 0; i < positionCodes.length; i++) {
        const positionCode = positionCodes[i];

        if (!positionCode) continue;

        // Validate fields for each position code
        if (!positionCode.positionCodeTitle) {
          errors[`positionCode${i}_positionCodeTitle`] = "Title is required.";
        }
        if (!positionCode.referenceLookbackYears) {
          errors[`positionCode${i}_referenceLookbackYears`] =
            "Years are required.";
        }

        const selectedServiceIDs = Array.from(
          selectedServices.get(positionCode.positionCodeID) || [],
        );
        if (selectedServiceIDs.length === 0) {
          errors[`positionCode${i}_services`] =
            "At least one service must be selected.";
        }
      }

      setValidationErrors((prevErrors) => ({ ...prevErrors, ...errors }));

      if (Object.keys(errors).length > 0) return;

      // Save all position codes (only the ones with changes)
      for (const positionCode of positionCodes) {
        const ogPC = OGpositionCodes?.find(
          (pc) => pc.positionCodeID === positionCode.positionCodeID,
        );
        const currentPCS =
          selectedServices.get(positionCode.positionCodeID) ??
          new Set<number>();
        const ogPCS =
          OGselectedServices.get(positionCode.positionCodeID) ??
          new Set<number>();

        const isNew = localPositionCodeIDs.has(positionCode.positionCodeID);

        if (isNew) {
          const created = await createPositionCode(positionCode);
          await updateServicesForPositionCode(
            created.positionCodeID,
            Array.from(currentPCS),
          );
          continue;
        }

        const pcChanged = !positionCodeEquals(positionCode, ogPC);
        const pcsChanged = !setsEqual(currentPCS, ogPCS);

        if (!pcChanged && !pcsChanged) {
          continue;
        }

        if (pcChanged) {
          await updatePositionCode(positionCode);
        }

        if (pcsChanged) {
          await updateServicesForPositionCode(
            positionCode.positionCodeID,
            Array.from(currentPCS),
          );
        }
      }
      //send email to admin
      await notifyAdminOfClientUpdate(client.clientID, "Position Codes");
      setEditing(false);
      setLoadingSpinner(false);
      setSuccessMessage(`All position codes saved successfully.`);
      window.location.reload();
    } catch (error) {
      console.error("Error saving position codes:", error);
      setSuccessMessage("");
    }
  };

  const handleDelete = async (positionCodeID: number) => {
    try {
      if (client?.clientID == null) return;

      setLoadingSpinner(true);

      if (localPositionCodeIDs.has(positionCodeID)) {
        setPositionCodes(
          (prevCodes) =>
            prevCodes?.filter(
              (code) => code.positionCodeID !== positionCodeID,
            ) || [],
        );
        setLocalPositionCodeIDs((prevIDs) => {
          const updatedIDs = new Set(prevIDs);
          updatedIDs.delete(positionCodeID);
          return updatedIDs;
        });
        setSuccessMessage("Position Code deleted successfully.");
      } else {
        await deletePositionCode(positionCodeID);
        setPositionCodes(
          (prevCodes) =>
            prevCodes?.filter(
              (code) => code.positionCodeID !== positionCodeID,
            ) || [],
        );
        setSuccessMessage("Position Code deleted successfully.");
        await notifyAdminOfClientUpdate(client.clientID, "Position Codes");
      }
    } catch (error) {
      console.error("Error deleting position code:", error);
      setSuccessMessage("");
    } finally {
      setDeleteTargetId(null);
      setEditing(false);
      setLoadingSpinner(false);
    }
  };

  const handleCreatePositionCode = () => {
    setEditing(true);
    const newPositionCode: PositionCode = {
      positionCodeID: Date.now() % 2147483647,
      clientID: client?.clientID || 0,
      positionCodeTitle: "",
      fcraCompliance: true,
      criminalLookbackYears: 7,
      referenceLookbackYears: 10,
      requiresCandidatePayment: false,
    };

    setPositionCodes((prevCodes) => [...(prevCodes || []), newPositionCode]);
    setLocalPositionCodeIDs((prevIDs) =>
      new Set(prevIDs).add(newPositionCode.positionCodeID),
    );

    setValidationErrors((prevErrors) => ({
      ...prevErrors,
      [`positionCode${positionCodes?.length}_positionCodeTitle`]:
        "Title is required.",
    }));
  };

  const renderServices = (
    positionCodeID: number,
    requiresCandidatePayment: boolean,
  ) => (
    <>
      <div className="services-list">
        {allServices.map((service) => (
          <div key={service.serviceID} className="service-item">
            <label>
              <input
                style={{ marginRight: "10px" }}
                className={`form-check-input ${
                  validationErrors[`positionCode${positionCodeID}_services`]
                    ? "is-invalid"
                    : ""
                } ${requiresCandidatePayment ? "disabled" : ""}`}
                type="checkbox"
                checked={
                  selectedServices
                    .get(positionCodeID)
                    ?.has(service.serviceID) || false
                }
                onChange={() =>
                  handleCheckboxChange(positionCodeID, service.serviceID)
                }
                disabled={requiresCandidatePayment}
              />
              {service.serviceName}
            </label>
          </div>
        ))}
      </div>

      {validationErrors[`positionCode${positionCodeID}_services`] && (
        <div className="invalid-feedback">
          {validationErrors[`positionCode${positionCodeID}_services`]}
        </div>
      )}
    </>
  );

  const handlePositionCodeChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    field: string,
    index: number,
  ) => {
    setEditing(true);
    const value =
      e.target.type === "checkbox"
        ? (e.target as HTMLInputElement).checked
        : e.target.value;

    setPositionCodes(
      (prevPositionCodes) =>
        prevPositionCodes?.map((positionCode, i) =>
          i === index ? { ...positionCode, [field]: value } : positionCode,
        ) || [],
    );

    setValidationErrors((prevErrors) => ({
      ...prevErrors,
      [`positionCode${index}_${field}`]: "",
    }));
  };

  return (
    <div className="content-container">
      <div className="header-container">
        <h1>Position Codes</h1>
        <div className="page-items-container">
          <div className="field-long">
            <label className="field-label">Client Name:</label>
            <div>
              <input
                className="form-control"
                type="text"
                value={client?.clientName || ""}
                disabled
                readOnly
              />
            </div>
          </div>
          <div className="field-long" style={{ flex: "0 0 40%" }}>
            <label className="field-label">Abbreviation/Client Number:</label>
            <div>
              <input
                className="form-control"
                type="text"
                value={
                  showAbbreviation
                    ? client?.abbreviation || ""
                    : "not available."
                }
                disabled
                readOnly
              />
            </div>
          </div>
          <button
            className="notification-button"
            onClick={() => setShowNotifications(true)}
          >
            <i className="bi bi-bell-fill"></i>
            {unreadCount > 0 && (
              <span className="notification-dot">{unreadCount}</span>
            )}
          </button>
        </div>
        {showNotifications && client?.clientID && (
          <NotificationsPopup
            notificationsInput={notifications}
            clientID={client?.clientID}
            onClose={(
              newUnreadCount = unreadCount,
              newNotifications = notifications,
            ) => {
              setNotifications(newNotifications);
              setShowNotifications(false);
              setUnreadCount(newUnreadCount);
            }}
          />
        )}
      </div>

      {loading ? (
        <div className="loading-divider">
          <div className="loading-bar"></div>
        </div>
      ) : (
        <>
          <div className="divider"></div>
          {!showAbbreviation && (
            <div className="text-container error">
              Please complete necessary forms in order to start ordering
              background screening and referencing. Then your
              abbreviation/client number will become visible.{" "}
            </div>
          )}

          <div className="data-container">
            {errorMessage && (
              <div className="error-message">{errorMessage}</div>
            )}
            <div className="text-body">
              If you're new to managing Position Codes or making updates, here's
              how to get started:
              <ol>
                <li>
                  <b>To Edit:</b> Select an existing Position Code and update
                  the description or services as needed.
                </li>
                <li>
                  <b>To Add:</b> Enter a new Position Code description (e.g.,
                  "Paid Staff" or "Volunteer") and select the services for that
                  position.
                </li>
                <li>
                  <b>Save Your Changes:</b> Once all updates are complete, click
                  'Save' to finalize.
                </li>
              </ol>
              You can define up to 10 Position Codes, and these can be adjusted
              at any time to reflect changes in your organization. If you need
              assistance, feel free to contact us.
            </div>

            {positionCodes && positionCodes.length > 0 ? (
              positionCodes.map((positionCode, index) => (
                <div
                  key={positionCode.positionCodeID}
                  className="position-codes-group"
                >
                  <div className="text-sub-header" style={{ flex: "0 0 100%" }}>
                    Position Code {index + 1}
                  </div>

                  <div className="field-long" style={{ flex: "0 0 60%" }}>
                    <label className="field-label">Title:</label>
                    <input
                      className={`form-control ${
                        validationErrors[
                          `positionCode${index}_positionCodeTitle`
                        ]
                          ? "is-invalid"
                          : ""
                      } ${
                        positionCode.requiresCandidatePayment ? "disabled" : ""
                      }`}
                      type="text"
                      value={positionCode.positionCodeTitle || ""}
                      onChange={(e) =>
                        handlePositionCodeChange(e, "positionCodeTitle", index)
                      }
                      disabled={positionCode.requiresCandidatePayment}
                    />
                    {validationErrors[
                      `positionCode${index}_positionCodeTitle`
                    ] && (
                      <div className="invalid-feedback">
                        {
                          validationErrors[
                            `positionCode${index}_positionCodeTitle`
                          ]
                        }
                      </div>
                    )}
                  </div>
                  <div
                    style={{
                      flex: "0 0 30%",
                      alignSelf: "end",
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "space-around",
                    }}
                  >
                    <button
                      className={`oxbtn btn-red ${
                        positionCode.requiresCandidatePayment ? "disabled" : ""
                      }`}
                      onClick={() =>
                        setDeleteTargetId(positionCode.positionCodeID)
                      }
                      disabled={positionCode.requiresCandidatePayment}
                    >
                      Delete
                    </button>
                  </div>

                  <div className="services-container">
                    <div className="text-sub-sub-header">
                      Services to be Performed:
                    </div>
                    {renderServices(
                      positionCode.positionCodeID,
                      positionCode.requiresCandidatePayment,
                    )}
                    {validationErrors[`${positionCode.positionCodeID}`] && (
                      <div
                        className="invalid-feedback"
                        style={{ display: "block" }}
                      >
                        {validationErrors[`${positionCode.positionCodeID}`]}
                      </div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <p>No position codes available for this client.</p>
            )}
            {deleteTargetId !== null && (
              <ConfirmDelete
                onConfirm={async () => {
                  await handleDelete(deleteTargetId);
                  setDeleteTargetId(null);
                }}
                onCancel={() => setDeleteTargetId(null)}
                message="Are you sure you want to delete this position code?"
              />
            )}

            {monitoringToRevert && (
              <ConfirmPopup
                onConfirm={() => {
                  setMonitoringToRevert(null);
                }}
                onCancel={() => {
                  handleCheckboxChange(monitoringToRevert.positionCodeID, 36);
                  setMonitoringToRevert(null);
                }}
                message={
                  <>
                    This service is billed at{" "}
                    <strong>$9 per candidate per month</strong> and includes
                    ongoing daily monitoring. To avoid charges for the following
                    month, monitoring must be canceled by the{" "}
                    <strong>25th of the current month</strong>.
                    <br />
                    <br />
                    By clicking <strong>Yes, I'm Sure</strong>, you agree to
                    enroll selected candidate(s) with this Position Code in
                    Continuous Criminal Monitoring. A new{" "}
                    <strong>Subscription Candidates</strong> menu option will be
                    available to view and manage candidates enrolled in this
                    service.
                  </>
                }
                btnColor="green"
              />
            )}

            <div className="action-buttons" style={{ flex: "0 0 100%" }}>
              <button
                className={`oxbtn btn-green ${!editing ? "disabled" : ""}`}
                onClick={() => handleSave()}
                disabled={!editing}
              >
                Save
              </button>
              {positionCodes && positionCodes.length < 10 && (
                <button
                  className="oxbtn btn-purple"
                  onClick={() => handleCreatePositionCode()}
                >
                  Add Additional Position Code{" "}
                  <i className="bi bi-plus-lg btn-icon"></i>
                </button>
              )}
            </div>
          </div>

          {successMessage && (
            <div className="alert-message alert alert-success alert-dismissible fade show">
              {successMessage}
              <button
                type="button"
                className="btn-close"
                onClick={() => setSuccessMessage("")}
                aria-label="Close"
              ></button>
            </div>
          )}
          {errorMessage && (
            <div
              className="alert alert-danger alert-dismissible fade show"
              role="alert"
            >
              {errorMessage}
              <button
                type="button"
                className="btn-close"
                onClick={() => setErrorMessage("")}
                aria-label="Close"
              ></button>
            </div>
          )}
          {loadingSpinner && (
            <div className="loading-mask">
              <div className="loading-spinner"></div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PositionCodes;
